import { useQuery } from '@apollo/client'
import { Stack } from '@mui/material'
import {
    GetRosterForClientQuery,
    ProcessStatusGroup,
    RosterStatus,
    RosterType,
} from '__generated__/graphql'
import CandidateDrawer from 'client/components/CandidateDrawer/CandidateDrawer'
import ClientIdentityProvider from 'client/contexts/ClientIdentityContext'
import RosterTableProvider from 'client/contexts/RosterTableContext'
import { GetRosterForClient } from 'client/graphql/search.gql'
import { useIdentity } from 'client/hooks'
import { SearchCandidateListRouteParams } from 'client/routes'
import { clientRouteConfig } from 'client/shared/routeTargets'
import { useCallback, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { QueryParamsProvider } from 'recruiter/contexts'
import { CandidateSqlSearchProvider } from 'client/candidate-table-temp/contexts/CandidateSqlSearchContext'
import { useCandidateSqlSearch } from 'client/candidate-table-temp/hooks/useCandidateSqlSearch'
import { LoadingPanel } from 'shared/components/LoadingPanel'
import useToast from 'shared/components/Toast/useToast'
import { usePageTitle } from 'shared/hooks/usePageTitle'
import { getPxAsRem } from 'shared/utils'
import ClientRosterCardPreview, {
    RosterListProps,
} from '../ClientRosterCardPreview'
import { DetailsRowProps } from '../DetailsRow'
import CandidatesSearchTable from './CandidatesSearchTable'

export interface RosterSearchCandidateProps {}

export const ClientVisibleProcessStatusGroups = [
    ProcessStatusGroup.Submitted,
    ProcessStatusGroup.InProcess,
    ProcessStatusGroup.OutOfProcess,
]

const RosterSearchCandidateList: React.FC<RosterSearchCandidateProps> = () => {
    const navigate = useNavigate()
    const toast = useToast()
    const currentRosterId =
        useParams<SearchCandidateListRouteParams>().rosterId ?? ''
    const identity = useIdentity()
    const [canViewRoster, setCanViewRoster] = useState(false)
    const { data: rosterData, loading: rosterLoad } = useQuery(
        GetRosterForClient,
        {
            variables: { rosterId: currentRosterId ?? '' },
            skip: !currentRosterId,
            onError: () => {
                toast({
                    title: 'Error',
                    message: 'Unable to access search, please try again',
                    severity: 'error',
                })
                navigate(`${clientRouteConfig.searches.path}`)
            },
        }
    )

    usePageTitle(rosterData?.getRosterForClient?.rosterName ?? undefined)

    useCallback(() => {
        const roster = rosterData?.getRosterForClient
        const currentStatus = roster?.status ?? RosterStatus.Draft
        const clientHasAccess =
            (roster?.sharedClients ?? []).filter(
                (sharedClient) =>
                    sharedClient?.client?.id &&
                    sharedClient.client.id === identity?.client?.id
            ).length > 0

        const hasPageAccess =
            clientHasAccess &&
            (currentStatus === RosterStatus.Active ||
                currentStatus === RosterStatus.Closed)
        setCanViewRoster(hasPageAccess)
    }, [identity, rosterData])

    useCallback(() => {
        if (!canViewRoster) {
            toast({
                title: 'Error',
                message: 'Unable to access search, please try again',
                severity: 'error',
            })
            navigate(`${clientRouteConfig.searches.path}`)
        }
    }, [navigate, toast, canViewRoster])

    return (
        <ClientIdentityProvider>
            <RosterTableProvider>
                <QueryParamsProvider prefix="client-roster-candidates">
                    <CandidateSqlSearchProvider>
                        <Stack
                            display={'flex'}
                            width={'100%'}
                            minWidth={getPxAsRem(375)}
                            padding={getPxAsRem(24, 24)}
                            gap={getPxAsRem(3, 24)}
                            spacing={3}
                            direction="column"
                            flex={1}
                            height={'100%'}
                            alignItems={'flex-start'}
                            overflow={'hidden'}
                        >
                            {rosterLoad ? (
                                <LoadingPanel />
                            ) : (
                                <>
                                    <ClientRosterCardPreview
                                        {...getRosterSearchPreviewProps(
                                            rosterData
                                        )}
                                    />
                                    <CandidatesSearchTable
                                        rosterId={currentRosterId}
                                    />
                                    <CandidateDrawerWithUrlSync />
                                </>
                            )}
                        </Stack>
                    </CandidateSqlSearchProvider>
                </QueryParamsProvider>
            </RosterTableProvider>
        </ClientIdentityProvider>
    )
}

const getRosterSearchPreviewProps = (
    rosterData: GetRosterForClientQuery | undefined
): RosterListProps => {
    return {
        details: {
            sharedRecruiters:
                rosterData?.getRosterForClient?.sharedRecruiters?.map(
                    ({ recruiter }) => ({
                        id: recruiter?.id || '',
                        preferredName: recruiter?.user?.preferredName || '',
                        profileImageKey: recruiter?.user?.profileImageKey || '',
                    })
                ) || [],
            sharedClients:
                rosterData?.getRosterForClient?.sharedClients?.map(
                    ({ client }) => ({
                        id: client?.id || '',
                        preferredName: client?.user?.preferredName || '',
                        profileImageKey: client?.user?.profileImageKey || '',
                    })
                ) || [],
            positions:
                rosterData?.getRosterForClient?.placements?.map(
                    (placement) => placement?.title?.value ?? ''
                ) || [],
            startDateLabel:
                rosterData?.getRosterForClient?.startDateLabel ?? '',
            officeLocations:
                rosterData?.getRosterForClient?.companyOffices?.map(
                    (office) =>
                        office?.companyOffice?.officeLocation?.value ?? ''
                ) || [],
            industries: rosterData?.getRosterForClient?.industries?.map(
                (industry) => industry?.industry?.value ?? ''
            ),
            sectors: rosterData?.getRosterForClient?.sectors?.map(
                (sector) => sector?.sector?.value ?? ''
            ),
            hideOnShrink: false,
        } as DetailsRowProps,
        rosterName: rosterData?.getRosterForClient?.rosterName,
        rosterId: rosterData?.getRosterForClient?.id ?? '',
        rosterType:
            rosterData?.getRosterForClient?.rosterType ?? RosterType.Search,
        statusGroupsForGraph: ClientVisibleProcessStatusGroups,
        isSelectable: false,
        showRosterType: false,
    }
}

function CandidateDrawerWithUrlSync() {
    const { rosterCandidateId, setRosterCandidateId } = useCandidateSqlSearch()
    return (
        <CandidateDrawer
            rosterCandidateId={rosterCandidateId}
            onClose={() => setRosterCandidateId(undefined)}
        />
    )
}

export default RosterSearchCandidateList
